'use client';

import { HStack, Box } from '@terra/ui-styled-system';
import { memo, useMemo, useState } from 'react';
import { BodyDefault } from '../../../components/typography';
import { ChatMessageHeader } from './message/ChatMessage';
import type { UserInfo } from '../types';
import { MessageSkeletonCard } from './MessageSkeletonCard';
import { UserMessageCardAttachments } from './UserMessageCardAttachments';
import type { TerraUIMessage } from '@terra/shared-dto-chat-types';
import {
  getMentionEntitiesFromUserMessage,
  getMentionSegmentsFromUserMessage,
  getUserMessageDisplayText,
} from '../utils/userMessageDisplayText';
import { InlineMessageContent } from './InlineMessageContent';
import { MentionContextPill } from '../context-pill/MentionContextPill';
import { useMentions } from '../mentions/ChatMentionsProvider';
import {
  InlineUserMessageEditor,
  type InlineUserMessageEditorProps,
} from './InlineUserMessageEditor';
import {
  UserMessageCardActions,
  type UserMessageCheckpointActions,
} from './UserMessageCardActions';

const INTERACTIVE_DESCENDANT_SELECTOR = 'button, a, [role="button"]';

export type UserMessageCardProps = {
  uiMessage: TerraUIMessage;
  user: UserInfo | undefined;
  noHeader?: boolean;
  isLoading?: boolean;
  onEditUserMessage?: () => void;
  inlineEditor?: Omit<InlineUserMessageEditorProps, 'uiMessage'>;
  checkpointActions?: UserMessageCheckpointActions;
};

export const UserMessageCard = memo(function UserMessageCard({
  uiMessage,
  user,
  noHeader,
  onEditUserMessage,
  inlineEditor,
  checkpointActions,
}: UserMessageCardProps) {
  const { onMentionClick } = useMentions();
  const [isHovered, setIsHovered] = useState(false);
  const header = useMemo(() => {
    if (noHeader) return null;

    return (
      <ChatMessageHeader
        name={user?.name || 'You'}
        role={user?.role}
        avatarSrc={user?.avatar}
      />
    );
  }, [user, noHeader]);
  const segments = useMemo(
    () => getMentionSegmentsFromUserMessage({ message: uiMessage }),
    [uiMessage],
  );
  const textPartsString = useMemo(() => {
    if (segments) return null;

    return getUserMessageDisplayText({ message: uiMessage });
  }, [uiMessage, segments]);
  const mentionEntities = useMemo(
    () => getMentionEntitiesFromUserMessage({ message: uiMessage }),
    [uiMessage],
  );
  const mentionEntitiesById = useMemo(() => {
    return new Map(
      mentionEntities.map((mention) => [mention.entityId, mention]),
    );
  }, [mentionEntities]);

  if (inlineEditor) {
    return <InlineUserMessageEditor uiMessage={uiMessage} {...inlineEditor} />;
  }

  const hasActions = Boolean(onEditUserMessage || checkpointActions);
  const handleCardClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (
      !onEditUserMessage ||
      (event.target instanceof Element &&
        event.target.closest(INTERACTIVE_DESCENDANT_SELECTOR))
    ) {
      return;
    }

    onEditUserMessage();
  };

  return (
    <Box
      position="relative"
      width="full"
      onMouseEnter={hasActions ? () => setIsHovered(true) : undefined}
      onMouseLeave={hasActions ? () => setIsHovered(false) : undefined}
    >
      <Box
        cursor={onEditUserMessage ? 'text' : undefined}
        onClick={onEditUserMessage ? handleCardClick : undefined}
      >
        <MessageSkeletonCard type="user" header={header} footer={null}>
          <UserMessageCardAttachments message={uiMessage} />
          {!segments && mentionEntities.length > 0 ? (
            <HStack mb={2} gap={1} flexWrap="wrap">
              {mentionEntities.map((mention) => (
                <MentionContextPill
                  key={mention.entityId}
                  entityId={mention.entityId}
                  entityType={mention.entityType}
                  label={mention.label}
                  extraData={mention.extraData}
                  onClick={onMentionClick}
                />
              ))}
            </HStack>
          ) : null}
          {segments ? (
            <InlineMessageContent
              segments={segments}
              mentionEntitiesById={mentionEntitiesById}
            />
          ) : (
            <BodyDefault
              color="fg.muted"
              m={0}
              whiteSpace="pre-wrap"
              wordBreak="break-word"
            >
              {textPartsString}
            </BodyDefault>
          )}
        </MessageSkeletonCard>
      </Box>
      {hasActions ? (
        <UserMessageCardActions
          isVisible={isHovered}
          onEditUserMessage={onEditUserMessage}
          checkpointActions={checkpointActions}
        />
      ) : null}
    </Box>
  );
});
